import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import api from '../../utils/api';
import useAutocomplete from '../../hooks/useAutocomplete';
import { isValidCoordinate, looksJammed } from '../../utils/geo';
import { collectionEditRecord, isUnreachable } from '../../utils/collectionRecord';

//: A blank identification, and a blank vegetation row. Private on purpose --
//  callers ask for a cleared form rather than reaching for these.
const EMPTY_IDENT = {
  taxon_name: '', taxonomic_rank: '', confidence: '',
  identification_method: '', expert_approval: false, notes: '',
};
const EMPTY_VEG = { species: '', is_dominant: false, vegetation_species_id: null };

/**
 * One collection, opened.
 *
 * The detail modal's whole world: the record, its identification history, its
 * vegetation, the edit form over the top of it, and the two deletes.
 *
 * The rule it shares with the wizard, and the reason `enqueue` is a parameter
 * here too: an edit is queued only when NOTHING answered. A 403 means this
 * person may not make this edit, and pushing it through /sync -- which upserts
 * what it is handed and checks none of it -- would be a way around that.
 *
 * It also owns the deep link. Arriving with an openCollectionId in the
 * navigation state opens that collection and then clears the state, so a
 * later back or forward does not reopen it.
 */
export default function useCollectionDetail({ t, user, enqueue, reload, setQueuedNotice }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [detail, setDetail]               = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  //: Every plate/slant/DNA/run under this sample (GET .../family), or null.
  const [family, setFamily]               = useState(null);
  const [editing, setEditing]             = useState(false);
  const [editForm, setEditForm]           = useState({});
  // The region box in the detail modal (hooks/useAutocomplete.js). `onType`
  // clears the chosen region as soon as the text is edited: the code that was
  // picked stops being what the box says.
  const editRegion = useAutocomplete({
    search: term => api.get(`/regions/search?q=${encodeURIComponent(term)}`)
      .then(res => res.data || []),
    onType: () => setEditForm(f => ({ ...f, region: '' })),
    onChoose: (loc) => {
      const value = loc.name_en || loc.name_he;
      setEditForm(f => ({ ...f, region: value }));
      lastConfirmedEditRegionRef.current = { query: loc.name_he, value };
    },
  });
  const lastConfirmedEditRegionRef                         = useRef({ query: '', value: '' });
  const [showEditAddRegionForm, setShowEditAddRegionForm] = useState(false);
  const editRegionLookupSeqRef                              = useRef(0);
  const [saving, setSaving]               = useState(false);
  const [saveError, setSaveError]         = useState('');
  const [deleteVegTarget, setDeleteVegTarget]     = useState(null);
  const [deletingVeg, setDeletingVeg]             = useState(false);

  // Identification history
  const [identHistory, setIdentHistory]   = useState([]);
  const [identLoading, setIdentLoading]   = useState(false);
  const [showIdentForm, setShowIdentForm] = useState(false);
  const [identForm, setIdentForm]         = useState(EMPTY_IDENT);
  const [identSaving, setIdentSaving]     = useState(false);
  const [identError, setIdentError]       = useState('');
  const [confirmingIdent, setConfirmingIdent] = useState(false);
  const [confirmIdentError, setConfirmIdentError] = useState('');

  // Vegetation (detail modal)
  const [vegetation, setVegetation]       = useState([]);
  const [vegLoading, setVegLoading]       = useState(false);
  const [showVegForm, setShowVegForm]     = useState(false);
  const [vegForm, setVegForm]             = useState(EMPTY_VEG);
  const [vegSaving, setVegSaving]         = useState(false);
  const [vegError, setVegError]           = useState('');

  // The same box in the detail modal, where picking one SETS the field being
  // edited rather than adding to a list.
  const editVeg = useAutocomplete({
    search: term => api.get(`/vegetation-species/search?q=${encodeURIComponent(term)}`)
      .then(res => res.data || []),
    onType: value => setVegForm(f => ({ ...f, species: value, vegetation_species_id: null })),
    onChoose: sp => setVegForm(f => ({
      ...f, species: sp.scientific_name, vegetation_species_id: sp.species_id,
    })),
  });
  const [showEditAddVegForm, setShowEditAddVegForm] = useState(false);
  const editVegLookupSeqRef                              = useRef(0);

  const resetEditVegAutocomplete = () => {
    editVeg.reset();
    setShowEditAddVegForm(false);
    editVegLookupSeqRef.current++;
  };

  // ---- Detail modal helpers ----

  const fetchIdents = (sampleId) => {
    setIdentLoading(true);
    api.get(`/collections/${sampleId}/identifications`)
      .then(res => setIdentHistory(res.data))
      .catch(() => {})
      .finally(() => setIdentLoading(false));
  };

  const fetchVegetation = (sampleId) => {
    setVegLoading(true);
    api.get(`/collections/${sampleId}/vegetation`)
      .then(res => setVegetation(res.data))
      .catch(() => {})
      .finally(() => setVegLoading(false));
  };

  const openDetail = (sampleId) => {
    setDetailLoading(true);
    setDetail(null); setEditing(false); setSaveError('');
    setIdentHistory([]); setShowIdentForm(false); setIdentForm(EMPTY_IDENT); setIdentError('');
    setVegetation([]); setShowVegForm(false); setVegForm(EMPTY_VEG); setVegError('');
    resetEditVegAutocomplete();
    setFamily(null);
    api.get(`/collections/${sampleId}`)
      .then(res => { setDetail(res.data); fetchIdents(sampleId); fetchVegetation(sampleId); })
      .catch(() => {})
      .finally(() => setDetailLoading(false));
    // From main: every plate, slant, DNA isolate and sequencing run under
    // this sample, so scanning the sample's own QR code reaches all of them.
    // Its own request on purpose -- a failure here leaves the detail view
    // working, just without the related list.
    api.get(`/collections/${sampleId}/family`)
      .then(res => setFamily(res.data))
      .catch(() => setFamily(null));
  };

  // Arrived via a notification deep link — open that collection directly,
  // then clear the nav state so a later back/forward doesn't reopen it.
  // A shared link (/collections?open=<id>, utils/recordShare.js) opens it the
  // same way; the server still decides what this reader may see of it.
  useEffect(() => {
    const fromLink = new URLSearchParams(location.search).get('open');
    if (location.state?.openCollectionId || fromLink) {
      openDetail(location.state?.openCollectionId || fromLink);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.search]);

  const startEdit = () => {
    setEditForm({
      qr_uid:             detail.qr_uid             || '',
      substrate:          detail.substrate           || '',
      fruiting_body_size: detail.fruiting_body_size  || '',
      phenological_stage: detail.phenological_stage  || '',
      fruiting_body_count: detail.fruiting_body_count ?? '',
      growth_pattern:     detail.growth_pattern       || '',
      fruiting_body_size_estimated: detail.fruiting_body_size_estimated ?? false,
      gps_lat:            detail.gps_lat             || '',
      gps_lng:            detail.gps_lng             || '',
      current_taxon_name: detail.current_taxon_name  || '',
      current_rank:       detail.current_rank        || '',
      current_confidence: detail.current_confidence  || '',
      lab_status:         detail.lab_status          || '',
      general_notes:      detail.general_notes       || '',
      region:             detail.region              || '',
    });
    editRegion.reset(detail.region || '');
    lastConfirmedEditRegionRef.current = { query: detail.region || '', value: detail.region || '' };
    setShowEditAddRegionForm(false);
    editRegionLookupSeqRef.current++; // invalidate any in-flight add-region POST from a previous edit session
    setSaveError(''); setEditing(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEditRegionSelect = loc => editRegion.choose(loc, { text: loc.name_he });

  const submitEdit = (e) => {
    e.preventDefault();
    setSaving(true); setSaveError(''); setQueuedNotice('');
    const sampleId = detail.sample_id;
    const payload = Object.fromEntries(Object.entries(editForm).filter(([, v]) => v !== ''));
    api.put(`/collections/${sampleId}`, payload)
      .then(() => { setEditing(false); openDetail(sampleId); reload(); })
      .catch(err => {
        // Same rule as the wizard: queue only when nothing answered. A 403
        // here means this person may not make this edit, and sending it
        // through /sync instead would be a way around that.
        if (!isUnreachable(err)) {
          setSaveError(err.response?.data?.error || t('editForm.saveFailed'));
          return;
        }
        return enqueue('field_collection', collectionEditRecord(sampleId, payload))
          .then(() => { setEditing(false); setQueuedNotice(t('editForm.queuedEdit')); closeModal(); });
      })
      .finally(() => setSaving(false));
  };

  const closeModal = () => {
    setDetail(null); setEditing(false); setSaveError('');
    setIdentHistory([]); setShowIdentForm(false); setIdentForm(EMPTY_IDENT); setIdentError('');
    setVegetation([]); setShowVegForm(false); setVegForm(EMPTY_VEG); setVegError('');
    editRegion.reset();
    lastConfirmedEditRegionRef.current = { query: '', value: '' };
    setShowEditAddRegionForm(false);
    editRegionLookupSeqRef.current++; // invalidate any in-flight add-region POST when the modal closes
    resetEditVegAutocomplete();
  };

  const recaptureGPS = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);
        const payload = { gps_lat: lat, gps_lng: lng };
        api.put(`/collections/${detail.sample_id}`, payload)
          .then(() => openDetail(detail.sample_id))
          .catch(() => {});
        // Warning only, and only when there is a reference point to
        // judge against -- see looksJammed. The record is already saved.
        if (!isValidCoordinate(lat, lng)) alert(t('detail.gpsInvalid'));
        else if (looksJammed(lat, lng)) alert(t('detail.gpsImplausible'));
      },
      () => alert(t('detail.gpsFetchFailed')),
      { timeout: 12000, enableHighAccuracy: true }
    );
  };

  const handleIdentChange = (e) => {
    const { name, value } = e.target;
    setIdentForm(f => ({ ...f, [name]: value }));
  };

  const submitIdent = (e) => {
    e.preventDefault();
    if (!identForm.taxon_name || !identForm.taxonomic_rank || !identForm.confidence || !identForm.identification_method) {
      setIdentError(t('detail.identValidationError')); return;
    }
    setIdentSaving(true); setIdentError('');
    api.post(`/collections/${detail.sample_id}/identifications`, identForm)
      .then(() => { setShowIdentForm(false); setIdentForm(EMPTY_IDENT); fetchIdents(detail.sample_id); })
      .catch(err => setIdentError(err.response?.data?.error || t('editForm.saveFailed')))
      .finally(() => setIdentSaving(false));
  };

  // One-click confirm of the collection's current taxon/rank/confidence — no
  // retyping. "Needs review" instead opens the existing manual identification
  // form (below) so a manager can log a corrected identification themselves.
  const confirmIdentification = () => {
    setConfirmingIdent(true);
    setConfirmIdentError('');
    api.post(`/collections/${detail.sample_id}/identifications`, {
      taxon_name: detail.current_taxon_name,
      taxonomic_rank: detail.current_rank,
      confidence: detail.current_confidence,
      identification_method: 'field',
      expert_approval: true,
      approved_by: user?.user_id,
    })
      .then(() => fetchIdents(detail.sample_id))
      .catch(err => setConfirmIdentError(err.response?.data?.error || t('editForm.saveFailed')))
      .finally(() => setConfirmingIdent(false));
  };

  const handleVegChange = (e) => {
    const { name, value, type, checked } = e.target;
    setVegForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const submitVeg = (e) => {
    e.preventDefault();
    if (!vegForm.species) { setVegError(t('editForm.vegAddFailed')); return; }
    setVegSaving(true); setVegError('');
    api.post(`/collections/${detail.sample_id}/vegetation`, vegForm)
      .then(() => { setShowVegForm(false); setVegForm(EMPTY_VEG); fetchVegetation(detail.sample_id); })
      .catch(err => setVegError(err.response?.data?.error || t('editForm.saveFailed')))
      .finally(() => setVegSaving(false));
  };

  const handleEditVegQueryChange = editVeg.onChange;
  const selectEditVegSpecies = sp => editVeg.choose(sp, { text: sp.scientific_name });

  const confirmDeleteVeg = () => {
    setDeletingVeg(true);
    api.delete(`/vegetation/${deleteVegTarget}`)
      .then(() => { setDeleteVegTarget(null); fetchVegetation(detail.sample_id); })
      .catch(() => setDeleteVegTarget(null))
      .finally(() => setDeletingVeg(false));
  };

  // The modal's DangerZone confirms first and shows a refusal itself
  // (its `failedText` is detail.deleteFailed); this only does the delete.
  const deleteCollection = () => api.delete(`/collections/${detail.sample_id}`)
    .then(() => { closeModal(); reload(); });
  // Opening a sub-form always clears what the last one left behind, so these
  // are helpers rather than three copies of the same three setters in JSX.
  const toggleVegForm = () => {
    setShowVegForm(s => !s);
    setVegError('');
    setVegForm(EMPTY_VEG);
    resetEditVegAutocomplete();
  };

  const toggleIdentForm = () => {
    setShowIdentForm(s => !s);
    setIdentError('');
    setIdentForm(EMPTY_IDENT);
  };

  const openIdentForm = () => {
    setShowIdentForm(true);
    setIdentError('');
    setIdentForm(EMPTY_IDENT);
  };

  return {
    // the record
    detail, detailLoading, family, open: openDetail, close: closeModal,
    // editing it
    editing, setEditing, startEdit, editForm, setEditForm,
    onEditChange: handleEditChange, submitEdit,
    saving, saveError, recaptureGPS,
    editRegion, onEditRegionPick: handleEditRegionSelect,
    lastConfirmedEditRegionRef, editRegionSeqRef: editRegionLookupSeqRef,
    addingRegion: showEditAddRegionForm, setAddingRegion: setShowEditAddRegionForm,
    // its identifications
    identHistory, identLoading, showIdentForm, toggleIdentForm, openIdentForm,
    identForm, setIdentForm, onIdentChange: handleIdentChange,
    submitIdent, identSaving, identError,
    confirmIdentification, confirmingIdent, confirmIdentError,
    // its vegetation
    vegetation, vegLoading, showVegForm, toggleVegForm,
    vegForm, onVegChange: handleVegChange, submitVeg, vegSaving, vegError,
    editVeg, onEditVegInput: handleEditVegQueryChange, onEditVegPick: selectEditVegSpecies,
    editVegSeqRef: editVegLookupSeqRef,
    addingVeg: showEditAddVegForm, setAddingVeg: setShowEditAddVegForm,
    // the two deletes
    deleteVegTarget, setDeleteVegTarget, deletingVeg, confirmDeleteVeg,
    // deleteCollection is what the modal's DangerZone confirms: a promise,
    // offered only when the record's `actions.delete` says so.
    deleteCollection,
  };
}
